'use client'

import React, { useCallback, useRef, useState } from 'react'

const LENS_SIZE = 120
const ZOOM_FACTOR = 2.5

type ProductImageMagnifierProps = {
  src: string
  alt: string
  children?: React.ReactNode
}

type ImageRect = {
  left: number
  top: number
  width: number
  height: number
}

function getRenderedImageRect(container: HTMLElement, img: HTMLImageElement): ImageRect | null {
  const containerRect = container.getBoundingClientRect()
  const naturalWidth = img.naturalWidth
  const naturalHeight = img.naturalHeight
  if (!naturalWidth || !naturalHeight) return null

  const containerAspect = containerRect.width / containerRect.height
  const imageAspect = naturalWidth / naturalHeight

  let renderedWidth: number
  let renderedHeight: number

  if (imageAspect > containerAspect) {
    renderedWidth = containerRect.width
    renderedHeight = containerRect.width / imageAspect
  } else {
    renderedHeight = containerRect.height
    renderedWidth = containerRect.height * imageAspect
  }

  return {
    left: (containerRect.width - renderedWidth) / 2,
    top: (containerRect.height - renderedHeight) / 2,
    width: renderedWidth,
    height: renderedHeight,
  }
}

function clampBackgroundPosition(
  desired: number,
  paneSize: number,
  backgroundSize: number,
): number {
  if (backgroundSize <= paneSize) {
    return (paneSize - backgroundSize) / 2
  }
  return Math.max(paneSize - backgroundSize, Math.min(0, desired))
}

export function ProductImageMagnifier({ src, alt, children }: ProductImageMagnifierProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const zoomPaneRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [isActive, setIsActive] = useState(false)
  const [lensStyle, setLensStyle] = useState<React.CSSProperties>({ display: 'none' })
  const [zoomPaneStyle, setZoomPaneStyle] = useState<React.CSSProperties>({ display: 'none' })

  const hideZoom = useCallback(() => {
    setIsActive(false)
    setLensStyle({ display: 'none' })
    setZoomPaneStyle({ display: 'none' })
  }, [])

  const updateZoom = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current
      const img = imgRef.current
      if (!container || !img || !img.naturalWidth) return

      const containerRect = container.getBoundingClientRect()
      const imageRect = getRenderedImageRect(container, img)
      if (!imageRect) return

      const relX = clientX - containerRect.left - imageRect.left
      const relY = clientY - containerRect.top - imageRect.top

      if (relX < 0 || relY < 0 || relX > imageRect.width || relY > imageRect.height) {
        hideZoom()
        return
      }

      const lensHalf = LENS_SIZE / 2
      const lensLeft =
        Math.max(0, Math.min(relX - lensHalf, imageRect.width - LENS_SIZE)) + imageRect.left
      const lensTop =
        Math.max(0, Math.min(relY - lensHalf, imageRect.height - LENS_SIZE)) + imageRect.top

      const lensCenterX = lensLeft - imageRect.left + LENS_SIZE / 2
      const lensCenterY = lensTop - imageRect.top + LENS_SIZE / 2

      const paneW = zoomPaneRef.current?.clientWidth || containerRect.width
      const paneH = zoomPaneRef.current?.clientHeight || containerRect.height

      const bgWidth = imageRect.width * ZOOM_FACTOR
      const bgHeight = imageRect.height * ZOOM_FACTOR

      const desiredBgPosX = paneW / 2 - lensCenterX * ZOOM_FACTOR
      const desiredBgPosY = paneH / 2 - lensCenterY * ZOOM_FACTOR
      const bgPosX = clampBackgroundPosition(desiredBgPosX, paneW, bgWidth)
      const bgPosY = clampBackgroundPosition(desiredBgPosY, paneH, bgHeight)

      setIsActive(true)
      setLensStyle({
        display: 'block',
        left: lensLeft,
        top: lensTop,
        width: LENS_SIZE,
        height: LENS_SIZE,
      })
      setZoomPaneStyle({
        display: 'block',
        backgroundImage: `url("${src}")`,
        backgroundSize: `${bgWidth}px ${bgHeight}px`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
        backgroundRepeat: 'no-repeat',
      })
    },
    [src, hideZoom],
  )

  const handleMouseMove = (e: React.MouseEvent) => updateZoom(e.clientX, e.clientY)
  const handleMouseLeave = () => hideZoom()

  return (
    <div className="relative overflow-visible">
      <div
        ref={containerRef}
        className="relative aspect-square w-full max-w-lg overflow-hidden rounded-lg bg-neutral-50 cursor-crosshair"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        role="img"
        aria-label={alt}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          className="absolute inset-0 h-full w-full object-contain object-center pointer-events-none select-none"
          draggable={false}
        />
        <div
          className="pointer-events-none absolute border border-neutral-400/80 bg-white/20"
          style={lensStyle}
          aria-hidden
        />
        {children}
      </div>
      <div
        ref={zoomPaneRef}
        className={`pointer-events-none absolute left-[calc(100%+1rem)] top-0 z-20 aspect-square w-full max-w-lg overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 shadow-sm ${isActive ? 'block' : 'hidden'}`}
        style={zoomPaneStyle}
        aria-hidden={!isActive}
      />
    </div>
  )
}
